import React, { useEffect, useMemo } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ---------- CARTO API KEY ---------- */
const CARTO_KEY = process.env.REACT_APP_CARTO_MAP_KEY;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ---------- Custom numbered pin ---------- */
const pinIcon = (color, label) =>
  L.divIcon({
    className: "",
    html: `
      <div style="position: relative; width: 36px; height: 46px;">
        <div style="
          width: 36px; height: 36px; background: ${color};
          border: 3px solid #FFFFFF; border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 6px 14px ${color}55, 0 2px 4px rgba(0,0,0,0.15);
          display: flex; align-items: center; justify-content: center;
          position: absolute; top: 4px; left: 0;
        ">
          <span style="
            transform: rotate(45deg); color: #FFFFFF;
            font-weight: 700; font-size: 13px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            line-height: 1;
          ">${label}</span>
        </div>
      </div>
    `,
    iconSize: [36, 46],
    iconAnchor: [18, 42],
    popupAnchor: [0, -38],
  });

const carerIcon = pinIcon("#1570EF", "1");
const clientIcon = pinIcon("#EF4444", "2");

/* ---------- Fit bounds ---------- */
const FitBounds = ({ bounds, maxZoom = 18 }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds?.length === 2) {
      map.fitBounds(bounds, { padding: [70, 70], maxZoom });
    }
  }, [bounds, map, maxZoom]);
  return null;
};

/* ---------- Zoom + Reset controls (inside map) ---------- */
const MapControls = ({ bounds }) => {
  const map = useMap();

  const handleZoomIn = (e) => {
    e.stopPropagation();
    map.zoomIn();
  };
  const handleZoomOut = (e) => {
    e.stopPropagation();
    map.zoomOut();
  };
  const handleReset = (e) => {
    e.stopPropagation();
    if (bounds?.length === 2) {
      map.fitBounds(bounds, { padding: [70, 70], maxZoom: 18 });
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {/* Zoom In */}
      <Tooltip title="Zoom in" placement="left" arrow>
        <IconButton
          onClick={handleZoomIn}
          size="small"
          sx={{
            width: 36,
            height: 36,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(6px)",
            border: "1px solid #EAECF0",
            boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
            color: "#101828",
            "&:hover": { bgcolor: "#FFFFFF", color: "#1570EF" },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Zoom Out */}
      <Tooltip title="Zoom out" placement="left" arrow>
        <IconButton
          onClick={handleZoomOut}
          size="small"
          sx={{
            width: 36,
            height: 36,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(6px)",
            border: "1px solid #EAECF0",
            boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
            color: "#101828",
            "&:hover": { bgcolor: "#FFFFFF", color: "#1570EF" },
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Reset / Fit view */}
      <Tooltip title="Reset view" placement="left" arrow>
        <IconButton
          onClick={handleReset}
          size="small"
          sx={{
            width: 36,
            height: 36,
            mt: 0.5,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(6px)",
            border: "1px solid #EAECF0",
            boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
            color: "#1570EF",
            "&:hover": { bgcolor: "#FFFFFF", color: "#0B4FB8" },
          }}
        >
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/* ---------- Distance helper (Haversine) ---------- */
const getDistanceKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const JobLocationMap = ({ carer, client, height = 420 }) => {
  const distance = useMemo(() => {
    if (
      !carer || !client ||
      typeof carer.lat !== "number" || typeof carer.lng !== "number" ||
      typeof client.lat !== "number" || typeof client.lng !== "number"
    ) return null;
    return getDistanceKm(carer, client);
  }, [carer, client]);

  if (!carer || !client) return null;

  const center = [
    (carer.lat + client.lat) / 2,
    (carer.lng + client.lng) / 2,
  ];

  const bounds = [
    [carer.lat, carer.lng],
    [client.lat, client.lng],
  ];

  /* ---------- CARTO tile URL with API key ----------
   * NOTE: CARTO basemaps expect `?key=...` (NOT `?api_key=...`).
   * Docs: https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=YOUR_KEY
   */
  const cartoUrl = CARTO_KEY
    ? `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`
    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  // Deeper zoom ceilings
  const tileMaxZoom = CARTO_KEY ? 20 : 19;   // native tile availability
  const mapMaxZoom = 22;                      // allow over-zoom (upscaled tiles)

  return (
    <Box
      sx={{
        width: "100%",
        height,
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #EAECF0",
        position: "relative",
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
        "& .leaflet-container": {
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          background: "#F8FAFC",
        },
        "& .leaflet-popup-content-wrapper": {
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
          border: "1px solid #EAECF0",
        },
        "& .leaflet-popup-content": {
          margin: "12px 14px",
          fontSize: "0.8rem",
          lineHeight: 1.5,
        },
      }}
    >
      <MapContainer
        key={`${carer.lat}-${carer.lng}-${client.lat}-${client.lng}`}
        center={center}
        zoom={16}
        minZoom={3}
        maxZoom={mapMaxZoom}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom
        doubleClickZoom
        touchZoom
        boxZoom
        keyboard
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url={cartoUrl}
          maxNativeZoom={tileMaxZoom}
          maxZoom={mapMaxZoom}
          subdomains={CARTO_KEY ? "abcd" : "abc"}
          attribution={
            CARTO_KEY
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          }
        />

        <FitBounds bounds={bounds} maxZoom={18} />
        <MapControls bounds={bounds} />

        {/* Soft halos */}
        <Circle
          center={[carer.lat, carer.lng]}
          radius={60}
          pathOptions={{
            color: "#1570EF",
            fillColor: "#1570EF",
            fillOpacity: 0.08,
            weight: 0,
          }}
        />
        <Circle
          center={[client.lat, client.lng]}
          radius={60}
          pathOptions={{
            color: "#EF4444",
            fillColor: "#EF4444",
            fillOpacity: 0.08,
            weight: 0,
          }}
        />

        {/* Route line */}
        <Polyline
          positions={[
            [carer.lat, carer.lng],
            [client.lat, client.lng],
          ]}
          pathOptions={{
            color: "#1570EF",
            weight: 3,
            dashArray: "10 8",
            opacity: 0.75,
          }}
        />

        {/* Carer marker */}
        <Marker position={[carer.lat, carer.lng]} icon={carerIcon}>
          <Popup>
            <Box sx={{ minWidth: 160 }}>
              <Box
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#1570EF",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                ● Carer
              </Box>
              <Box sx={{ fontWeight: 600, color: "#101828" }}>
                {carer.name || "Unknown"}
              </Box>
              {carer.address && (
                <Box sx={{ color: "#667085", mt: 0.25 }}>
                  {carer.address}
                </Box>
              )}
            </Box>
          </Popup>
        </Marker>

        {/* Client marker */}
        <Marker position={[client.lat, client.lng]} icon={clientIcon}>
          <Popup>
            <Box sx={{ minWidth: 160 }}>
              <Box
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#EF4444",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                ● Client
              </Box>
              <Box sx={{ fontWeight: 600, color: "#101828" }}>
                {client.name || "Unknown"}
              </Box>
              {client.address && (
                <Box sx={{ color: "#667085", mt: 0.25 }}>
                  {client.address}
                </Box>
              )}
            </Box>
          </Popup>
        </Marker>
      </MapContainer>

      {/* ---------- Legend (bottom-left) ---------- */}
      <Box
        sx={{
          position: "absolute",
          bottom: 12,
          left: 12,
          zIndex: 1000,
          bgcolor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(6px)",
          borderRadius: "10px",
          border: "1px solid #EAECF0",
          boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
          px: 1.5,
          py: 1.25,
          minWidth: 180,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
          <Box
            sx={{
              width: 18, height: 18, borderRadius: "50%",
              bgcolor: "#1570EF", color: "#fff",
              fontSize: "0.65rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            1
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#101828", lineHeight: 1.2 }}>
              Carer
            </Box>
            <Box
              sx={{
                fontSize: "0.65rem", color: "#667085", lineHeight: 1.2,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                maxWidth: 130,
              }}
            >
              {carer.name || "—"}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 18, height: 18, borderRadius: "50%",
              bgcolor: "#EF4444", color: "#fff",
              fontSize: "0.65rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            2
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#101828", lineHeight: 1.2 }}>
              Client
            </Box>
            <Box
              sx={{
                fontSize: "0.65rem", color: "#667085", lineHeight: 1.2,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                maxWidth: 130,
              }}
            >
              {client.name || "—"}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ---------- Distance badge (top-left) ---------- */}
      {distance !== null && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 1000,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(6px)",
            borderRadius: "10px",
            border: "1px solid #EAECF0",
            boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
            px: 1.5,
            py: 0.75,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.6rem",
              fontWeight: 600,
              color: "#667085",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: 1.2,
            }}
          >
            Distance
          </Typography>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "#101828",
              lineHeight: 1.2,
            }}
          >
            {distance < 1
              ? `${Math.round(distance * 1000)} m`
              : `${distance.toFixed(1)} km`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default JobLocationMap;